import { Alert, Skeleton, Stack, Typography } from "@mui/material"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    activateUser,
    CompaniesByIdentifierMode,
    CompaniesByIdentifierResponse,
    fetchCompaniesByIdentifier,
    SelfSignUpApi,
} from "../selfSignUpApi"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { getInitialCompanySelection, transformCompanies } from "../../../login/components/idCardSignup/helpers"
import { Form, Formik, FormikProps } from "formik"
import { SelfSignUpForm } from "./selfSignUpTypes"
import { SelfSignUpFormContentCompany } from "../components/SelfSignUpFormContentCompany"
import { ConfirmationRow } from "../components/ConfirmationRow"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import * as yup from "yup"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { usePublicApiMutation } from "../../../../core/api/useApiMutation"
import { useEffect, useMemo, useRef, useState } from "react"
import { Player } from "@lottiefiles/react-lottie-player"
import loadingAnimationSuccess from "../../../login/assets/success-lottie-anim.json"
import { makeStyles } from "tss-react/mui"
import { ErrorDisplay } from "../../../../core/api/errorTypes"
import { ConfirmModal } from "../../../../shared/components/dialogs/ConfirmModal"

const transformCardSubscriptionCompanyData = ({ companies, merchant }: CompaniesByIdentifierResponse) => {
    if (!companies) return { companies: [], merchant }

    return { companies: transformCompanies(companies), merchant }
}

const useStyles = makeStyles()(() => ({
    animation: {
        height: "8rem",
        marginBottom: "2rem",
    },
    confirmaDialogContent: {
        paddingBottom: "3rem",
    },
    confirmError: {
        width: "100%",
    },
}))
export function SelfSignUp() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { systemId } = useParams<{ systemId: string }>()
    const [isComplete, setIsComplete] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [confirmErrorMessage, setConfirmErrorMessage] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [isConfirmLoading, setIsConfirmLoading] = useState(false)
    const formikRef = useRef<FormikProps<SelfSignUpForm>>(null)

    const {
        data = { companies: [], merchant: undefined },
        isLoading,
        isError,
        error,
    } = useApiQuery(fetchCompaniesByIdentifier, {
        queryName: SelfSignUpApi.COMPANIES_BY_IDENTIFIER,
        dependencies: {
            mode: CompaniesByIdentifierMode.MERCHANT_SIGNUP,
            identifier: systemId || "",
        },
        errorDisplay: ErrorDisplay.NONE,
        enabled: !!systemId,
        select: transformCardSubscriptionCompanyData,
    })

    useEffect(() => {
        if (isError) {
            const message = getDisplayMessage(error, l10n.getString("self-sign-up-error-message"))
            setErrorMessage(message)
        }
    }, [isError, error, l10n])

    const { mutateAsync: callActivateUser } = usePublicApiMutation(activateUser, {
        async onSuccess(_, data) {
            setIsComplete(true)
            setConfirmErrorMessage(null)

            const message = getDisplayMessage(data, null)
            setSuccessMessage(message)
        },
        async onError(_, error) {
            const message = getDisplayMessage(error, l10n.getString("self-sign-up-error-message"))
            setConfirmErrorMessage(message)
        },
    })

    const getDisplayMessage = (responseData: any, fallback: string | null): string | null => {
        if (!responseData || typeof responseData !== "object") {
            return fallback
        }

        if (responseData?.authenticationResult?.message) {
            return String(responseData?.authenticationResult?.message)
        }

        if (responseData?.serviceResponse?.displayMessage) {
            return String(responseData?.serviceResponse?.displayMessage)
        }

        if (responseData?.displayMessage) {
            return String(responseData?.displayMessage)
        }

        return fallback
    }

    const initialValues: SelfSignUpForm = useMemo(
        () => ({
            ...getInitialCompanySelection(data.companies),
            name: "",
            mobileNumber: "",
            email: "",
        }),
        [data.companies],
    )

    const validationSchema = yup.object({
        name: yup
            .string()
            .required(l10n.getString("required-field"))
            .max(50, l10n.getString("id-card-signup-field-name-max-length")),
        email: yup
            .string()
            .email(l10n.getString("self-sign-up-field-email-invalid"))
            .required(l10n.getString("required-field"))
            .max(50, l10n.getString("id-card-signup-field-name-max-length")),
        mobileNumber: yup.string().test({
            name: "is-valid-phonenumber",
            test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
            message: l10n.getString("id-card-signup-field-mobile-number-invalid"),
        }),
        companyId: yup.string().required(l10n.getString("required-field")),
        userGroupId: yup.string().required(l10n.getString("required-field")),
    })

    const onSubmit = () => {
        setConfirmErrorMessage(null)
        setShowConfirmDialog(true)
    }

    const handleConfirmCreateUser = async () => {
        const values = formikRef.current?.values
        if (!values) return

        setConfirmErrorMessage(null)
        setIsConfirmLoading(true)

        const selectedCompany = data.companies.find((company) => company?.id === values.companyId)
        // Company sign-up links carry no merchant; sending merchant: { id: null } fails the backend
        const merchantId = data.merchant?.id

        try {
            await callActivateUser({
                user: {
                    displayName: values.name,
                    mobileNumber: values.mobileNumber,
                    email: values.email,
                    userGroup: {
                        id: Number(values.userGroupId),
                    },
                    company: {
                        id: Number(selectedCompany?.companyId),
                    },
                },
                ...(merchantId ? { merchant: { id: Number(merchantId) } } : {}),
            })
            setShowConfirmDialog(false)
        } catch {
            // Error message is surfaced by the mutation's onError (confirmErrorMessage);
        } finally {
            setIsConfirmLoading(false)
        }
    }

    const handleCancelConfirm = () => {
        formikRef.current?.setSubmitting(false)
        setConfirmErrorMessage(null)
        setShowConfirmDialog(false)
    }

    const getConfirmationData = () => {
        const values = formikRef.current?.values
        if (!values) return null

        const selectedCompany = data.companies.find((company) => company?.id === values.companyId)

        let userGroupName = ""
        if (selectedCompany && !selectedCompany.isGroup) {
            const selectedUserGroup = selectedCompany.userGroups?.find(
                (group) => group.id === Number(values.userGroupId),
            )
            userGroupName = selectedUserGroup?.name || ""
        }

        return {
            companyName: selectedCompany?.displayName || "",
            userGroupName,
            name: values.name,
            email: values.email,
            mobileNumber: values.mobileNumber,
        }
    }

    const confirmData = getConfirmationData()

    return (
        <>
            {isComplete ? (
                <Stack alignItems="center">
                    <Player
                        keepLastFrame
                        src={loadingAnimationSuccess}
                        autoplay
                        speed={1}
                        className={classes.animation}
                    />
                    <LocalizedStrict id="self-sign-up-congratulation-title">
                        <Typography variant="h4" fontWeight={600} marginBottom="2rem" textAlign="center">
                            Congratulation
                        </Typography>
                    </LocalizedStrict>
                    {successMessage && (
                        <Typography variant="body1" textAlign="center" marginTop="1rem">
                            {successMessage}
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Stack alignItems="center">
                    <Formik<SelfSignUpForm>
                        innerRef={formikRef}
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                    >
                        <Form>
                            <Stack gap="2rem">
                                <Stack gap="1rem">
                                    <LocalizedStrict id="self-sign-up-title">
                                        <Typography variant="h4" fontWeight={600}>
                                            Sign Up
                                        </Typography>
                                    </LocalizedStrict>
                                    <LocalizedStrict id="self-sign-up-description">
                                        <Typography variant="body1">
                                            Please complete the registration process. Once you have finished signing up,
                                            you can use account
                                        </Typography>
                                    </LocalizedStrict>
                                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                                </Stack>

                                <Stack gap="1rem">
                                    {isLoading || (!data.companies.length && !isError) ? (
                                        <SelfSignUpLoadingState />
                                    ) : (
                                        <SelfSignUpFormContentCompany companies={data?.companies || []} />
                                    )}
                                </Stack>
                            </Stack>
                        </Form>
                    </Formik>
                </Stack>
            )}
            {showConfirmDialog && (
                <LocalizedStrict
                    id="self-sign-up-confirm-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                >
                    <ConfirmModal
                        title="Confirm details"
                        buttonLabelSubmit="Confirm"
                        buttonLabelCancel="Cancel"
                        handleClick={handleConfirmCreateUser}
                        open={showConfirmDialog}
                        onClose={handleCancelConfirm}
                        buttonColorSubmit="black"
                        buttonSubmitLoading={isConfirmLoading}
                        autoCloseAfterSubmit={false}
                        classNameOverride={classes.confirmaDialogContent}
                    >
                        {confirmErrorMessage && (
                            <Alert severity="error" className={classes.confirmError}>
                                {confirmErrorMessage}
                            </Alert>
                        )}
                        {confirmData && (
                            <Stack gap="0.5rem" width="100%" sx={{ textAlign: "left" }}>
                                <LocalizedStrict id="self-sign-up-confirm-company" attrs={{ label: true }}>
                                    <ConfirmationRow label="Company:" value={confirmData.companyName} />
                                </LocalizedStrict>
                                <LocalizedStrict id="self-sign-up-confirm-user-group" attrs={{ label: true }}>
                                    <ConfirmationRow label="User group:" value={confirmData.userGroupName} />
                                </LocalizedStrict>
                                <LocalizedStrict id="self-sign-up-confirm-name" attrs={{ label: true }}>
                                    <ConfirmationRow label="Your name:" value={confirmData.name} />
                                </LocalizedStrict>
                                <LocalizedStrict id="self-sign-up-confirm-email" attrs={{ label: true }}>
                                    <ConfirmationRow label="Your email:" value={confirmData.email} />
                                </LocalizedStrict>
                                <LocalizedStrict id="self-sign-up-confirm-mobile-number" attrs={{ label: true }}>
                                    <ConfirmationRow label="Your phone:" value={confirmData.mobileNumber} />
                                </LocalizedStrict>
                            </Stack>
                        )}
                    </ConfirmModal>
                </LocalizedStrict>
            )}
        </>
    )
}

export const SelfSignUpLoadingState = () => {
    return (
        <Stack gap="1rem">
            <Skeleton variant="rectangular" animation="wave" height={55} />
            <Skeleton variant="rectangular" animation="wave" height={55} />
            <Skeleton variant="rectangular" animation="wave" height={55} />
            <Skeleton variant="rectangular" animation="wave" height={55} />
        </Stack>
    )
}
